import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, Subscription } from 'rxjs';
import { CallShellState } from '@callcenter-app/service/call-state.machine';
import { CallStateService } from '@callcenter-app/service/call-state.service';
import { WORKPLACE_PATH } from '@callcenter-app/service/workplace.config';
import { CartTickerService } from '@callcenter-app/components/order-live/cart-ticker.service';
import { buildCallBarView, CallBarAction, CallBarView } from './call-bar.view';

/**
 * The band of the call under the header, on every page but the workplace (stage 4, board `CallBar`). It only reads `CallStateService.state$` and the address; the second counts on the shared one-second ticker (outside the zone,
 * repainting this view only, and only when a figure has changed). A click is an ordinary navigation: the guards of the editor ask their questions as they always do.
 */
@Component({
  selector: 'app-call-bar',
  templateUrl: './call-bar.component.html',
  styleUrls: ['./call-bar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgClass]
})
export class CallBarComponent implements OnInit, OnDestroy {
  view?: CallBarView;

  private state?: CallShellState;
  private url = '';
  private subs = new Subscription();
  private stopTicker?: () => void;
  private shown = '';
  private ready = false;

  constructor(
    private callState: CallStateService,
    private router: Router,
    private ticker: CartTickerService,
    private tr: TranslocoService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.url = this.router.url;
    this.subs.add(this.callState.state$.subscribe(state => {
      this.state = state;
      this.refresh(true);
    }));
    this.subs.add(this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
      this.url = e.urlAfterRedirects;
      this.refresh(true);
    }));
    this.subs.add(this.tr.langChanges$.subscribe(() => this.refresh(true)));
    // the first drawing is the one that follows `ngOnInit`
    this.ready = true;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopTicker?.();
    this.stopTicker = undefined;
  }

  go(action: CallBarAction): void {
    if (action === 'order') {
      const target = this.callState.openOrderTarget();
      if (target) {
        void this.router.navigate(target.commands, { queryParams: target.queryParams, state: target.state });
      }
      return;
    }
    void this.router.navigateByUrl(WORKPLACE_PATH);
  }

  hide(view: CallBarView): void {
    if (view.callId) {
      this.callState.hideBand(view.callId);
    }
  }

  /** `force`: the state or the address changed — the view is drawn anyway; the ticker only draws when the text of the view is not the one on the screen. */
  private refresh(force: boolean): void {
    if (!this.state) {
      return;
    }
    const target = this.callState.openOrderTarget();
    const view = buildCallBarView({
      state: this.state,
      url: this.url,
      nowMs: performance.now(),
      wallMs: Date.now(),
      orderOpen: this.callState.orderIsOpen(),
      hasOrderTarget: !!target,
      onOrderPage: !!target && pathOfCommands(target.commands) === this.url.split(/[?#]/)[0],
      lang: this.tr.getActiveLang(),
      tr: (key, params) => this.tr.translate(key, params),
    });
    const signature = view ? [view.kind, view.lead, view.who, view.meta, view.sub, view.primary?.label, view.second?.label].join('|') : '';
    if (!force && signature === this.shown) {
      return;
    }
    this.shown = signature;
    this.view = view;
    this.tickWhile(!!view);
    if (this.ready) {
      this.cdr.detectChanges();
    }
  }

  /** The one-second ticker is listened to only while a band is on the screen — a page with nothing to say runs no timer for it. */
  private tickWhile(needed: boolean): void {
    if (needed && !this.stopTicker) {
      this.stopTicker = this.ticker.subscribe(() => this.refresh(false));
    } else if (!needed && this.stopTicker) {
      this.stopTicker();
      this.stopTicker = undefined;
    }
  }
}

/** `['/order/new', 12]` → `/order/new/12`. */
export function pathOfCommands(commands: Array<string | number>): string {
  return commands.map(String).join('/').replace(/\/{2,}/g, '/');
}
